import { AxiosError } from 'axios';
import React, { createContext, useEffect, useState } from 'react';
import { FeatureToggleProvider } from 'react-feature-toggles';
import { FeatureFlagKeyValue, FeatureFlags, GenericObject } from 'v2/models';
import { handleError, ServerError } from 'v2/utils/handleError';
import restFactory from 'v2/utils/restFactory';
import { getUserRole } from 'v2/utils/user';

export type IFeatureFlagContext = {
  flags: Record<string, boolean>;
  isReady?: boolean;
};

const FeatureFlagContext = createContext<IFeatureFlagContext>({
  flags: {},
});

function useFeatureFlagContext() {
  const context = React.useContext(FeatureFlagContext);
  if (!context) {
    throw new Error(`useFeatureFlagContext must be used within a FeatureFlag`);
  }
  return context;
}

function FeatureFlagProvider(props: GenericObject) {
  const [flags, setFlags] = useState<FeatureFlagKeyValue>({});
  const [isReady, setIsReady] = useState<boolean>(false);
  const jwt = props.user?.jwt || '';
  const userRole = getUserRole(props.user?.user);
  useEffect(() => {
    async function loadFeatureFlags() {
      try {
        const url =
          process.env.REACT_APP_DRF_ENABLED === 'true'
            ? '/dj/api/v1/feature_flag/flattened'
            : '/feature-flags';
        if (jwt) {
          const response = await restFactory.get<FeatureFlags[]>(url);
          const flags: FeatureFlagKeyValue = response.reduce(
            (acc, flag) => ({
              ...acc,
              [flag.name]: flag.value,
            }),
            {}
          );

          setFlags(flags);
          setIsReady(true);
          props.onLoad?.(props.user.user);
        }
      } catch (error) {
        handleError(error as AxiosError<ServerError>);
      }
    }
    loadFeatureFlags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jwt, userRole]);

  return (
    <FeatureToggleProvider key={`${isReady}`} featureToggleList={flags}>
      <FeatureFlagContext.Provider {...props} value={{ flags, isReady }} />
    </FeatureToggleProvider>
  );
}

export { FeatureFlagProvider, FeatureFlagContext, useFeatureFlagContext };
